import React, { useState, useEffect, useRef } from 'react';
import './TechCarousel.css';
// Import technology icons
import { 
  SiPython, SiJavascript, SiReact, SiNodedotjs, SiHtml5, SiCss3,
  SiPostgresql, SiRedis, SiDocker, SiKubernetes, SiLinux, 
  SiNginx, SiAnsible, SiTerraform, SiGit, SiGooglecloud,
  SiFlask, SiNumpy, SiPandas, SiGrafana, SiC, SiGnubash,
  SiTraefikproxy, SiWireshark, SiGithub, SiGithubactions, SiAmazonwebservices
} from 'react-icons/si';
import { 
  FaServer,
  FaLayerGroup, FaRocket, FaLock, FaWindows
} from 'react-icons/fa';
import { PortainerIcon, MatlabIcon, AzureIcon, SocketIOIcon } from '../CustomIcons';
import { GiSnake } from 'react-icons/gi';

// Organized technology groups with labels
const technologyGroups = [
  {
    label: 'Cloud Platforms',
    technologies: [
      { name: 'GCP', color: '#4285f4', icon: SiGooglecloud },
      { name: 'AWS', color: '#FF9900', icon: SiAmazonwebservices },
      { name: 'Azure', color: '#0089d0', icon: AzureIcon },
    ]
  },
  {
    label: 'CI/CD',
    technologies: [
      { name: 'GitHub', color: '#181717', icon: SiGithub },
      { name: 'GitHub Actions', color: '#2088ff', icon: SiGithubactions },
    ]
  },
  {
    label: 'Databases',
    technologies: [
      { name: 'PostgreSQL', color: '#336791', icon: SiPostgresql },
      { name: 'Redis', color: '#dc382d', icon: SiRedis },
    ]
  },
  {
    label: 'Programming Languages',
    technologies: [
      { name: 'Python', color: '#3776ab', icon: SiPython },
      { name: 'JavaScript', color: '#f7df1e', icon: SiJavascript },
      { name: 'C/C++', color: '#00599c', icon: SiC },
      { name: 'Shell/Bash', color: '#4eaa25', icon: SiGnubash },
      { name: 'MATLAB', color: '#0076a8', icon: MatlabIcon },
    ]
  },
  {
    label: 'Web Technologies',
    technologies: [
      { name: 'React', color: '#61dafb', icon: SiReact },
      { name: 'Node.js', color: '#339933', icon: SiNodedotjs },
      { name: 'HTML', color: '#e34f26', icon: SiHtml5 },
      { name: 'CSS', color: '#1572b6', icon: SiCss3 },
      { name: 'Flask', color: '#000000', icon: SiFlask },
      { name: 'SocketIO', color: '#010101', icon: SocketIOIcon },
    ]
  },
  {
    label: 'Monitoring & Tools',
    technologies: [
      { name: 'Grafana', color: '#f46800', icon: SiGrafana },
      { name: 'Traefik', color: '#24a1c1', icon: SiTraefikproxy },
      { name: 'Wireshark', color: '#1679a7', icon: SiWireshark },
    ]
  },
  {
    label: 'Python Libraries',
    technologies: [
      { name: 'NumPy', color: '#013243', icon: SiNumpy },
      { name: 'Pandas', color: '#150458', icon: SiPandas },
      { name: 'Netmiko', color: '#8fbc8f', icon: GiSnake },
    ]
  },
  {
    label: 'Infrastructure',
    technologies: [
      { name: 'Docker', color: '#0db7ed', icon: SiDocker },
      { name: 'Portainer', color: '#13bef9', icon: PortainerIcon },
      { name: 'Kubernetes', color: '#326ce5', icon: SiKubernetes },
      { name: 'Linux', color: '#fcc624', icon: SiLinux },
      { name: 'Nginx', color: '#009639', icon: SiNginx },
      { name: 'Ansible', color: '#ee0000', icon: SiAnsible },
      { name: 'Terraform', color: '#7b42bc', icon: SiTerraform },
      { name: 'Git', color: '#f05032', icon: SiGit },
      { name: 'Windows Server', color: '#0078d4', icon: FaWindows },
      { name: 'LXC', color: '#f7941e', icon: FaLayerGroup },
      { name: 'Proxmox', color: '#e57000', icon: FaServer },
      { name: 'Teleport', color: '#512fc9', icon: FaLock },
      { name: 'Litestar', color: '#6c63ff', icon: FaRocket },
    ]
  }
];

// Create carousel items with grouped structure
const createCarouselItems = () => {
  const items = [];
  
  technologyGroups.forEach((group, groupIndex) => {
    items.push({
      type: 'group',
      content: group,
      id: `group-${groupIndex}`
    });
  });
  
  return items;
};

const TechCarousel = ({ isDarkMode = false }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [edgeState, setEdgeState] = useState('center'); // 'left', 'center', 'right'
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const containerRef = useRef(null);
  const trackRef = useRef(null);
  const animationRef = useRef(null);
  const positionRef = useRef(0);
  const [dimensions, setDimensions] = useState({ trackWidth: 0, containerWidth: 0 });
  
  // Create carousel items with grouped structure
  const carouselItems = createCarouselItems();
  // Create enough duplicates for truly seamless scrolling
  const duplicatedItems = Array(5).fill(carouselItems).flat().map((item, index) => ({
    ...item,
    uniqueId: `${item.id}-${Math.floor(index / carouselItems.length)}-${index % carouselItems.length}`
  }));
  
  const baseSpeed = 0.8; // Base scrolling speed
  const edgeSpeed = 1.8; // Manual scroll speed

  // Calculate dimensions and setup
  useEffect(() => {
    if (!containerRef.current || !trackRef.current) return;
    
    const updateDimensions = () => {
      const containerWidth = containerRef.current.offsetWidth;
      const trackWidth = trackRef.current.scrollWidth;
      
      setDimensions({ containerWidth, trackWidth });
      
      // Initialize position to start smoothly from the beginning
      if (positionRef.current === 0) {
        positionRef.current = 0;
        trackRef.current.style.transform = `translateX(0px)`;
      }
    };
    
    updateDimensions();
    
    const resizeObserver = new ResizeObserver(updateDimensions);
    resizeObserver.observe(containerRef.current);
    
    return () => resizeObserver.disconnect();
  }, [duplicatedItems.length]);

  // Detect touch device
  useEffect(() => {
    setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
  }, []);

  // Main animation loop with perfect seamless looping
  useEffect(() => {
    if (dimensions.trackWidth === 0) return;
    
    const singleSetWidth = dimensions.trackWidth / 5; // We have 5 copies
    
    const animate = () => {
      if (trackRef.current) {
        let speed = 0;
        
        if (!isHovered || isTouchDevice) {
          // Normal auto-scroll when not hovered or on touch devices
          speed = -baseSpeed;
        } else {
          // Manual control when hovered on non-touch devices
          if (edgeState === 'left') {
            speed = edgeSpeed; // Scroll right (positive)
          } else if (edgeState === 'right') {
            speed = -edgeSpeed; // Scroll left (negative)
          }
          // If edgeState === 'center', speed remains 0 (stopped)
        }
        
        // Apply movement
        if (speed !== 0) {
          positionRef.current += speed;
          
          // Seamless looping: reset position when we've scrolled one full set
          if (positionRef.current <= -singleSetWidth) {
            positionRef.current += singleSetWidth;
          }
          if (positionRef.current >= singleSetWidth) {
            positionRef.current -= singleSetWidth;
          }
        }
        
        // Apply transform
        trackRef.current.style.transform = `translateX(${positionRef.current}px)`;
      }
      
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isHovered, edgeState, dimensions, isTouchDevice]);

  // Mouse position tracking for edge detection (non-touch devices only)
  useEffect(() => {
    if (isTouchDevice) return;
    
    const handleMouseMove = (e) => {
      if (!isHovered || !containerRef.current) return;
      
      const rect = containerRef.current.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const containerWidth = rect.width;
      const edgeZone = 100; // 100px edge zones
      
      if (mouseX < edgeZone) {
        setEdgeState('left');
      } else if (mouseX > containerWidth - edgeZone) {
        setEdgeState('right');
      } else {
        setEdgeState('center');
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('mousemove', handleMouseMove);
      
      return () => {
        container.removeEventListener('mousemove', handleMouseMove);
      };
    }
  }, [isHovered, isTouchDevice]);

  // Touch handling for swipe gestures
  useEffect(() => {
    if (!isTouchDevice || dimensions.trackWidth === 0) return;

    let touchStartX = 0;
    let touchEndX = 0;
    const singleSetWidth = dimensions.trackWidth / 5;

    const handleTouchStart = (e) => {
      touchStartX = e.changedTouches[0].screenX;
    };

    const handleTouchEnd = (e) => {
      touchEndX = e.changedTouches[0].screenX;
      const swipeDistance = touchStartX - touchEndX;
      
      // Implement manual scroll on swipe
      if (Math.abs(swipeDistance) > 50) {
        const scrollAmount = swipeDistance > 0 ? -100 : 100;
        positionRef.current += scrollAmount;
        
        // Apply same seamless looping logic for touch
        if (positionRef.current <= -singleSetWidth) {
          positionRef.current += singleSetWidth;
        }
        if (positionRef.current >= singleSetWidth) {
          positionRef.current -= singleSetWidth;
        }
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('touchstart', handleTouchStart);
      container.addEventListener('touchend', handleTouchEnd);
      
      return () => {
        container.removeEventListener('touchstart', handleTouchStart);
        container.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isTouchDevice, dimensions]);

  // Handle hover state changes (non-touch devices only)
  const handleMouseEnter = () => {
    if (!isTouchDevice) {
      setIsHovered(true);
      setEdgeState('center'); // Start in center when first hovering
    }
  };

  const handleMouseLeave = () => {
    if (!isTouchDevice) {
      setIsHovered(false);
      setEdgeState('center');
    }
  };

  const getTechIcon = (tech) => {
    const IconComponent = tech.icon;
    return (
      <IconComponent 
        size={36}
        color={tech.color}
        className="tech-icon"
      />
    );
  };

  return (
    <div className="tech-carousel-container" ref={containerRef}>
      <div 
        className={`tech-carousel ${isHovered && !isTouchDevice ? 'hovered' : ''} ${edgeState} ${isTouchDevice ? 'touch-device' : ''}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="tech-carousel-track" ref={trackRef}>
          {duplicatedItems.map((item) => (
            <div key={item.uniqueId} className="tech-group">
              <div className="group-border">
                <div className="group-label-inside">
                  {item.content.label}
                </div>
                <div className="group-tech-items">
                  {item.content.technologies.map((tech, techIndex) => (
                    <div key={`${tech.name}-${techIndex}`} className="tech-item">
                      <div className="tech-logo">
                        {getTechIcon(tech)}
                      </div>
                      <div className="tech-name-tooltip">
                        {tech.name}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Fade out edges */}
        <div className="fade-edge fade-left"></div>
        <div className="fade-edge fade-right"></div>
      </div>
      
      <div className={`scroll-hint ${isHovered && !isTouchDevice ? 'visible' : ''}`}>
        {isHovered && !isTouchDevice ? (
          edgeState === 'left' ? 'Scrolling right ←' :
          edgeState === 'right' ? 'Scrolling left →' :
          'Move to edges to scroll'
        ) : isTouchDevice ? 'Swipe to navigate' : 'Hover to pause and control'}
      </div>
    </div>
  );
};

export default TechCarousel;
